import type { components } from '@trackercop/contracts';

type DetectedScript = components['schemas']['DetectedScript'];
type ScriptCategory = components['schemas']['ScriptCategory'];

// Severity ladder used to colour a domain node by its "most severe"
// script category. A domain that loads both a benign CDN asset and an
// advertising pixel should render as advertising, because that is what
// a reviewer cares about.
const CATEGORY_SEVERITY: Record<ScriptCategory, number> = {
  advertising: 6,
  marketing: 6,
  'session-replay': 5,
  'customer-data-platform': 5,
  analytics: 4,
  social: 4,
  'ab-testing': 3,
  'tag-manager': 3,
  'consent-management': 2,
  cdn: 1,
  unknown: 0,
};

export interface LineageNode {
  id: string;
  domain: string;
  scriptCount: number;
  category: ScriptCategory;
  hasBeforeConsent: boolean;
  scriptIds: string[];
}

export interface LineageEdge {
  source: string;
  target: string;
}

export interface LineageGraph {
  nodes: LineageNode[];
  edges: LineageEdge[];
}

function safeHostname(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    return new URL(url).hostname;
  } catch {
    return null;
  }
}

// Build a domain-aggregated directed graph from detected-script rows.
// - Nodes: one per distinct domain. Category = most severe script
//   category seen on that domain. `scriptCount` drives node radius.
// - Edges: de-duplicated `initiatorDomain -> scriptDomain` pairs.
// Self-loops (script on domain A loading another script on domain A)
// are dropped — they don't communicate anything a reviewer can act on
// and they cause the force layout to visually collapse.
export function buildLineageGraph(scripts: readonly DetectedScript[]): LineageGraph {
  const nodes = new Map<string, LineageNode>();
  const edgeSet = new Set<string>();
  const edges: LineageEdge[] = [];

  for (const s of scripts) {
    const domain = s.domain;
    const current = nodes.get(domain);
    if (!current) {
      nodes.set(domain, {
        id: domain,
        domain,
        scriptCount: 1,
        category: s.category,
        hasBeforeConsent: s.loadedBeforeConsent,
        scriptIds: [s.id],
      });
    } else {
      current.scriptCount += 1;
      current.scriptIds.push(s.id);
      if (s.loadedBeforeConsent) current.hasBeforeConsent = true;
      if (CATEGORY_SEVERITY[s.category] > CATEGORY_SEVERITY[current.category]) {
        current.category = s.category;
      }
    }
  }

  for (const s of scripts) {
    const initiatorDomain = safeHostname(s.initiatorUrl);
    if (!initiatorDomain) continue;
    if (initiatorDomain === s.domain) continue;
    if (!nodes.has(initiatorDomain)) continue;
    const key = `${initiatorDomain}\u0000${s.domain}`;
    if (edgeSet.has(key)) continue;
    edgeSet.add(key);
    edges.push({ source: initiatorDomain, target: s.domain });
  }

  const sortedNodes = Array.from(nodes.values()).sort((a, b) =>
    a.domain.localeCompare(b.domain),
  );

  return { nodes: sortedNodes, edges };
}

export function nodeRadius(scriptCount: number): number {
  const min = 8;
  const max = 30;
  const scaled = min + Math.sqrt(scriptCount) * 4;
  return Math.max(min, Math.min(max, scaled));
}

// Stable tailwind text colour per category — used by the side panel and
// the node fill. Kept adjacent to the graph helpers so the palette and
// the data transform stay in one file; the Session 8 palette covers
// category chips, but the graph uses solid fills (no /15 alpha) for
// legibility against the graph background.
export const lineageCategoryFill: Record<ScriptCategory, string> = {
  advertising: '#dc2626',
  marketing: '#e11d48',
  'session-replay': '#ea580c',
  'customer-data-platform': '#c026d3',
  analytics: '#f59e0b',
  social: '#7c3aed',
  'ab-testing': '#f59e0b',
  'tag-manager': '#4f46e5',
  'consent-management': '#16a34a',
  cdn: '#64748b',
  unknown: '#a3a3a3',
};
